<template>
  <div ref="ferroApp" id="ferro-app">
    <v-app>
      <!--      some sort of bug with clipped, mobile-breakpoint fixes it-->
      <v-navigation-drawer v-if="isLoggedIn" v-model="drawer" app clipped class="nav-drawer">
        <side-menu></side-menu>
        <template v-slot:append>
          <admin-side-menu v-if="isAnAdmin"></admin-side-menu>
        </template>
      </v-navigation-drawer>
      <global-navigation @toggle-drawer="drawer = !drawer"></global-navigation>
      <v-main app>
        <v-container fluid>
          <v-banner style="z-index: 100; position: fixed; width: 60%" v-if="showCodeSplittingRefresh" :elevation="20" color="warning">
            A new version of Ferro has been released and a refresh is required to continue.
            <template v-slot:actions>
              <v-btn text color="primary" @click="reloadPage">Reload</v-btn>
            </template>
          </v-banner>
          <v-breadcrumbs v-if="breadCrumbs.length" :items="breadCrumbs" divider="-"></v-breadcrumbs>
          <router-view></router-view>
        </v-container>
      </v-main>
      <v-snackbar v-model="alert.show" color="success" :timeout="alert.timeout ?? 5000">
        {{ alert.text }}

        <template v-slot:action="{ attrs }">
          <v-btn v-if="alert.action" color="white" text v-bind="attrs" @click="hideGlobalAlert(alert.action)"> Undo </v-btn>
          <v-btn v-else color="white" text v-bind="attrs" @click="hideGlobalAlert()"> Close </v-btn>
        </template>
      </v-snackbar>

      <v-snackbar v-model="errorAlert.show" color="error" :timeout="errorAlert.timeout ? errorAlert.timeout : 5000">
        {{ errorAlert.text }}

        <template v-slot:action="{ attrs }">
          <v-btn color="white" text v-bind="attrs" @click="hideGlobalErrorAlert()"> Close </v-btn>
        </template>
      </v-snackbar>

      <div style="z-index: 99999" ref="infoAlert" v-if="info.show" class="info-box text-caption">{{ info.text }}</div>

      <feature-dialog
        v-if="showFeatureDialog"
        @update-show="e => (showFeatureDialog = e)"
        :show-dialog="showFeatureDialog"
        :features="getFeatures"
      />
      <v-footer class="footer" color="primary" app>
        <span class="white--text">&copy; {{ new Date().getFullYear() }}</span>
        <a v-if="showFeaturesButton" @click="showFeatureDialog = true" class="ml-4 white--text">Features</a>
        <v-spacer />
        <a v-if="isLoggedIn && isBrokerageUser" class="white--text" @click="goToHelp">Need Help?</a>
      </v-footer>

      <v-card v-if="getLoading" color="white" align-center style="position: fixed; z-index: 9999; top: 0; width: 100%">
        <v-progress-linear indeterminate color="primary" class="mb-0" width="300"></v-progress-linear>
      </v-card>
    </v-app>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import GlobalNavigation from '@/components/GlobalNavigation'
import * as routes from '@/router/route-names'
import SideMenu from '@/components/sidemenu/SideMenu'
import AdminSideMenu from '@/components/sidemenu/AdminSideMenu'
import FeatureDialog from '@/components/admin/FeaturesDialog'
import { setTheme } from '@/components/whitelabel/white-label'

export default {
  components: { FeatureDialog, SideMenu, GlobalNavigation, AdminSideMenu },
  props: {
    source: String,
  },
  created() {
    this.ROUTES = routes
    setTheme()
  },
  data: () => ({
    drawer: null,
    alert: {
      show: false,
      text: undefined,
    },
    errorAlert: {
      show: false,
      text: undefined,
    },
    info: {
      show: false,
      text: undefined,
      evt: undefined,
    },
    showHelpDialog: false,
    showFeatureDialog: false,
  }),
  watch: {
    getGlobalAlert: function (val) {
      this.alert = val
    },
    getGlobalErrorAlert: function (val) {
      this.errorAlert = val
    },
    getGlobalInfo: function (val) {
      this.info = val
      this.$nextTick(() => {
        this.setInfoPosition(val.evt, val.text)
      })
    },
    async info(val) {
      if (val.show) {
        await new Promise(resolve => setTimeout(resolve, 4000))
        val.show = false
      }
    },
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'isLoggedIn',
      getUser: 'getUser',
      getActiveAccount: 'getActiveAccount',
      getGlobalAlert: 'getGlobalAlert',
      getGlobalErrorAlert: 'getGlobalErrorAlert',
      getGlobalInfo: 'getGlobalInfo',
      getLoading: 'getLoading',
      showCodeSplittingRefresh: 'showCodeSplittingRefresh',
      isAnAdmin: 'isAnAdmin',
      getFeatures: 'getFeatures',
      quoteFlowName: 'quoteFlowName',
      hasSideMenuOption: 'hasSideMenuOption',
      isBrokerageUser: 'isBrokerageUser',
    }),
    accountCode() {
      return this.$route.params.accountId
    },
    isOnDashboard() {
      return this.$route.name === routes.DASHBOARD
    },
    activeCompanyName() {
      const accountCode = this.getActiveAccount.accountCode
      // Certain paths are outside of the context of the account - displaying the account name doesn't make sense
      if (this.$route.path.includes(accountCode)) {
        return this.getActiveAccount && this.getActiveAccount.companyName
      }
      return ''
    },
    showFeaturesButton() {
      // Right now, this is for admin only...
      return this.activeCompanyName && this.isAnAdmin
    },
    // Route Names that should link back to the Renewal Dashboard
    renewalRouteNames() {
      return [
        routes.COMPANY_PROFILE,
        routes.COVERAGE_SUMMARY,
        routes.SCHEDULES,
        routes.SCHEDULES_TYPED,
        routes.APPLICATIONS,
        routes.EXPERIENCE_MOD,
        routes.LOSS_RUNS,
        routes.FINANCIALS,
        routes.UNDERWRITING_DOCUMENTS,
        routes.SUBMIT_TO_ACCOUNT_MANAGER,
        routes.ADVISOR_APP_CYCLE,
        routes.INTERNAL_QUOTE_REVIEW,
        routes.SUBJECTIVITIES,
        routes.QUOTE_REVIEW,
        routes.BIND_DECISION,
        routes.POLICY_SELECTION,
      ]
    },
    breadCrumbs() {
      const breadCrumbs = []
      const routePath = this.$route.path
      const accountCode = this.getActiveAccount.accountCode
      const brokerCode = this.$route.params.brokerCode
      const isInAdminSection = routePath.startsWith('/admin')
      if (isInAdminSection) {
        // Account Context bread crumbs do not make sense in the admin section of the site
        return breadCrumbs
      }

      let backLinkRoute = `/${accountCode}/dashboard`
      let backLinkName = 'Dashboard'
      if (routePath.match(/\/\w+\/applications\/\w+/)) {
        backLinkRoute = `/${accountCode}/applications`
        backLinkName = `Applications`
      }
      if (this.hasSideMenuOption('RenewalDashboard') && this.renewalRouteNames.includes(this.$route.name)) {
        backLinkRoute = `/${accountCode}/renewal`
        backLinkName = this.quoteFlowName
      } else if (routePath.includes('dashboard/risk-management') && this.$route.name !== routes.DASHBOARD_RISK_MANAGEMENT) {
        backLinkRoute = `/${accountCode}/dashboard/risk-management`
        backLinkName = 'Risk Mgmt'
      } else if (brokerCode && routePath.includes('brokerage-administration/')) {
        backLinkRoute = `/brokerage-administration/${brokerCode}`
        backLinkName = 'Broker Administration'
        breadCrumbs.push({
          text: backLinkName,
          disabled: false,
          to: backLinkRoute,
          exact: true,
        })
      }

      if (this.activeCompanyName && this.activeCompanyName.length > 0) {
        const maxAllowedLength = 25
        const maxLength = this.activeCompanyName.length > maxAllowedLength ? maxAllowedLength : this.activeCompanyName.length
        let companyName = this.activeCompanyName.substring(0, maxLength).trim()
        if (this.activeCompanyName.length > maxAllowedLength) {
          companyName += '...'
        }
        companyName += "'s"
        backLinkName = `${companyName} ${backLinkName}`
      }

      if (this.isLoggedIn && accountCode) {
        breadCrumbs.push({
          text: backLinkName,
          disabled: false,
          to: backLinkRoute,
          exact: true,
        })

        if (!this.isOnDashboard) {
          let text = this.$route.name
          if (text === routes.RENEWAL_DASHBOARD) {
            text = this.quoteFlowName
          }
          breadCrumbs.push({
            text: text,
            disabled: false,
            exact: true,
            to: this.$route.path,
          })
        }
      }
      return breadCrumbs
    },
  },
  methods: {
    ...mapActions({
      setGlobalAlert: 'setGlobalAlert',
      setGlobalErrorAlert: 'setGlobalErrorAlert',
      logout: 'logout',
    }),
    reloadPage() {
      window.location.reload()
    },
    hideGlobalAlert(func) {
      this.setGlobalAlert({
        text: undefined,
        show: false,
      })
      if (func) {
        func()
      }
    },
    hideGlobalErrorAlert() {
      this.setGlobalErrorAlert({
        text: undefined,
        show: false,
      })
    },
    goToHelp() {
      this.$router.push({
        name: routes.NEED_HELP,
      })
    },
    setInfoPosition(evt) {
      const info = this.$refs.infoAlert
      if (info) {
        if (evt != null) {
          const xPosition = evt.clientX - this.$refs.ferroApp.getBoundingClientRect().left
          const yPosition = evt.clientY - this.$refs.ferroApp.getBoundingClientRect().top
          info.style.left = xPosition + 'px'
          info.style.top = yPosition + 'px'
          info.style.marginTop = '0px'
        } else {
          info.style.position = 'fixed'
          info.style.left = '50%'
          info.style.top = '100px'
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import '~@/scss/variables.scss'; // Using this should get you the variables

#ferro-app {
  background-color: $light;
}
//z-index to go above excel tool on schedules - excel is 10,
// toolbars around excel are 11
// Top Bar and Footer are 12
.nav-drawer {
  z-index: 15 !important;
}

.footer {
  z-index: 12 !important;
}

.info-box {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 5px;
  border-radius: 5px;
}

/*this style applies globally*/
$striped-table-border-color: rgba(0, 0, 0, 0.25);
.striped-table {
  tbody tr:nth-of-type(odd) {
    background-color: rgba($primary, 0.1) !important;
  }
  th {
    border-bottom: thin solid $striped-table-border-color !important;
  }
  //tr:last-child
  //{
  //  td {
  //    border-bottom: thin solid $striped-table-border-color !important;
  //  }
  //}
  //td:first-child {
  //  border-left: thin solid $striped-table-border-color !important;
  //}
  //td:last-child {
  //  border-right: thin solid $striped-table-border-color !important;
  //}

  &.clickable tr:hover {
    opacity: 0.8;
    cursor: pointer;
  }
}

.medium-center-page {
  width: 90%;
  max-width: 950px;
  margin: auto;
  padding-bottom: 5rem;
}

.medium-page {
  padding-bottom: 5rem;
}

// This is a fix for the excel table top left corner
.systable thead th.first-col {
  z-index: 6 !important;
}
</style>
